<template>
    <v-expansion-panel
        class="income-needs-panel">
        <v-expansion-panel-header
            style="background-color: #F2F2F2;"
            class="subtitle-1 black--text subtitle-1 font-weight-medium">
            <div class="tooltip-wrap">
                <v-tooltip right>
                    <template v-slot:activator="{ on, attrs }">
                        <div
                            style="display: inline"
                            v-bind="attrs"
                            v-on="on"
                            class="list-tooltip">
                            {{ title }}
                            <v-icon small>mdi-help-rhombus</v-icon>
                        </div>
                    </template>
                    <span>{{ tooltipText('Portal - Income Planner - Target Income for Retirement') }}</span>
                </v-tooltip>
            </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content color="white">
            <div class="mt-5">
                <p class="mb-8 font-weight-medium">Monthly Gross Income Goal in Retirement</p>
                <p>
                <v-slider
                    v-model="form.monthlyIncomeNeeds"
                    :min="monthlyIncomeNeedsMin"
                    :max="monthlyIncomeNeedsMax"
                    :disabled="disabled"
                    step="100"
                    thumb-label="always"
                    @end="$emit('incomeNeedsUpdated', {payload: {monthlyIncomeNeeds: form.monthlyIncomeNeeds}})">
                    <template v-slot:prepend>
                        <span class="font-weight-light">{{ monthlyIncomeNeedsMin | money }}</span>
                    </template>
                    <template v-slot:append>
                        <span class="font-weight-light">{{ monthlyIncomeNeedsMax | money }}</span>
                    </template>
                </v-slider>
                </p>
            </div>
            <v-spacer></v-spacer>
            <div>
                <p class="d-sm-inline mb-2 font-weight-medium">Add Adjustment</p>
                <v-btn
                    :disabled="disabled"
                    :outlined="false"
                    x-small
                    rounded
                    @click="expandAdjustment = !expandAdjustment"
                    style="margin-left: 10px;">
                    <v-icon x-small v-show="expandAdjustment">mdi-minus</v-icon>
                    <v-icon x-small v-show="!expandAdjustment">mdi-plus</v-icon>
                </v-btn>
                <transition-group
                    v-show="expandAdjustment">
                    <div
                        key="adjustment-form"
                        class="income-adjustment mb-5 mt-2">
                        <p class="mb-0">
                            <v-btn
                                :disabled="disabled"
                                x-small
                                icon
                                style="float: right"
                                @click="$emit('adjustmentUpdated', {payload: {...form.newAdjustment}, action: 'add'})">
                                <v-icon small>mdi-plus</v-icon>
                            </v-btn>
                        </p>
                        <p class="mb-0">
                            <v-text-field
                                :disabled="disabled"
                                v-model="form.newAdjustment.description"
                                prepend-icon="mdi-account-circle"
                                single-line
                                placeholder="Custom"
                                label="Name"></v-text-field>
                        </p>
                        <div class="d-flex">
                            <v-select
                                v-model="form.newAdjustment.direction"
                                :disabled="disabled"
                                :items="adjustmentDirectionOptions"
                                class="justify-space-between"
                                label="Adjustment"
                                style="margin-right: 8px"/>
                            <v-text-field
                                v-model="form.newAdjustment.value"
                                :disabled="disabled"
                                class="justify-space-between"
                                label="Amount"
                                prefix="$"
                                style="margin-right: 8px"/>
                            <v-select
                                v-model="form.newAdjustment.start_year"
                                :disabled="disabled"
                                :items="relativePlanYearOptions"
                                label="From year"
                                class="justify-space-between"
                                style="margin-right: 8px"/>
                            <v-select
                                v-model="form.newAdjustment.end_year"
                                :disabled="disabled"
                                :items="relativePlanYearOptions"
                                class="justify-space-between"
                                label="To year"
                                style="margin-right: 8px"/>
                        </div>
                    </div>
                    <v-divider key="divider"></v-divider>
                    <div
                        key="adjustment-list"
                        class="mt-5 income-adjustment-list">
                        <div
                            v-for="adjustment in form.adjustments"
                            :key="adjustment.id || adjustment.key"
                            class="income-adjustment font-weight-light mb-2">
                            <p>
                                <v-btn
                                    v-if="adjustment.id"
                                    :disabled="disabled"
                                    style="float: right"
                                    x-small
                                    icon
                                    @click="$emit('adjustmentUpdated', {payload: {id: adjustment.id}, action: 'delete'})">
                                    <v-icon small>mdi-delete</v-icon>
                                </v-btn>
                                <v-btn
                                    v-if="adjustment.id"
                                    :disabled="disabled"
                                    x-small
                                    icon
                                    style="float: right"
                                    @click="$emit('adjustmentUpdated', {payload: {...adjustment}, action: 'update'})">
                                    <v-icon small>mdi-content-save</v-icon>
                                </v-btn>
                                <v-btn
                                    v-if="!adjustment.id"
                                    :disabled="disabled"
                                    x-small
                                    icon
                                    style="float: right"
                                    @click="$emit('adjustmentUpdated', {payload: {...adjustment, household_id: householdId}, action: 'add-default'})">
                                    <v-icon small>mdi-plus</v-icon>
                                </v-btn>
                            </p>
                            <p>
                                <v-text-field
                                    v-model="adjustment.description"
                                    :readonly="defaultFields.includes(adjustment.description)"
                                    :disabled="disabled"
                                    :prepend-icon="icons(adjustment.description)"
                                    single-line
                                    placeholder="Name"
                                    label="Name"></v-text-field>
                            </p>
                            <div class="d-flex">
                                <v-select
                                    v-model="adjustment.direction"
                                    :disabled="disabled"
                                    :items="adjustmentDirectionOptions"
                                    class="justify-space-between"
                                    label="Adjustment"
                                    style="margin-right: 8px; flex-basis: 100px;"/>
                                <v-text-field
                                    v-model="adjustment.value"
                                    :disabled="disabled"
                                    class="justify-space-between"
                                    label="Amount"
                                    prefix="$"
                                    style="margin-right: 8px; flex-basis: 100px;"/>
                                <v-select
                                    v-model="adjustment.start_year"
                                    :disabled="disabled"
                                    :items="relativePlanYearOptions"
                                    label="From year"
                                    class="justify-space-between"
                                    style="margin-right: 8px; flex-basis: 100px;"/>
                                <v-select
                                    v-model="adjustment.end_year"
                                    :disabled="disabled"
                                    :items="relativePlanYearOptions"
                                    class="justify-space-between"
                                    label="To year"
                                    style="margin-right: 8px; flex-basis: 100px;"/>
                            </div>
                        </div>
                    </div>
                </transition-group>
            </div>
        </v-expansion-panel-content>
    </v-expansion-panel>
</template>

<style>
    .income-needs-panel .v-text-field {
        padding-top: 0px;
    }

    .income-adjustment {
        border: solid lightgray 1px;
        padding: 10px;
    }
    </style>
<script>
const MONTHLY_INCOME_DEFAULT_MIN = 3000;
const MONTHLY_INCOME_DEFAULT_MAX = 25000;
export default {
    props: {
        description: {
            type: String
        },
        monthlyIncomeNeeds: {
            type: Number,
            required: false
        },
        adjustments: {
            type: Array,
            required: false
        },
        relativePlanYearDropdown: {
            type: Object,
            required: false
        },
        disabled: {
            type: Boolean,
            default: false
        },
        householdId: {
            type: Number
        },
    },

    computed: {
        monthlyIncomeNeedsMin() {
            if (this.monthlyIncomeNeeds == null || (this.monthlyIncomeNeeds / 2) < MONTHLY_INCOME_DEFAULT_MIN) {
                return MONTHLY_INCOME_DEFAULT_MIN;
            }
            return Math.round(this.monthlyIncomeNeeds / 2 / 100) * 100;
        },
        monthlyIncomeNeedsMax() {
            return this.monthlyIncomeNeeds == null ? MONTHLY_INCOME_DEFAULT_MAX :  this.monthlyIncomeNeeds * 2;
        },
        relativePlanYearOptions() {
            let items = []
            for (let year in this.relativePlanYearDropdown) {
                let item = {};
                item.text = this.relativePlanYearDropdown[year];
                item.value = parseInt(year);
                items.push(item);
            }
            return items;
        },
    },

    inject: ['tooltipText'],

    data() {
        return {
            form: {
                monthlyIncomeNeeds: this.monthlyIncomeNeeds == null ? MONTHLY_INCOME_DEFAULT_MIN : this.monthlyIncomeNeeds,
                adjustments: [...this.adjustments],
                newAdjustment: {
                    'description': null,
                    'direction': null,
                    'start_year': null,
                    'end_year': null,
                    'value': null,
                    'household_id': this.householdId
                },
            },
            adjustmentDirectionOptions: [
                {
                    text: 'Increase',
                    value: 0
                },
                {
                    text: 'Decrease',
                    value: 1
                }
            ],
            defaultFields: [
                'Health Insurance',
                'Education Expense',
                'Travel Budget'
            ],
            expandAdjustment: false,
            title: 'Target Income for Retirement'
        }
    },

    watch: {
        monthlyIncomeNeeds() {
            this.form.monthlyIncomeNeeds = this.monthlyIncomeNeeds;
        },
        adjustments() {
            //New adjustment added - reset new form
            if (this.form.adjustments.length < this.adjustments.length) {
                this.form.newAdjustment =  {
                    'description': null,
                    'direction': null,
                    'start_year': null,
                    'end_year': null,
                    'value': null,
                    'household_id': this.householdId
                };
            }
            this.form.adjustments = [... this.adjustments];

        },
    },

    methods: {
        icons(key) {
            let icons = {
                'Education Expense': 'mdi-school-outline',
                'Health Insurance': 'mdi-hospital-box-outline',
                'Travel Budget': 'mdi-wallet-travel',
                'Other': 'mdi-account-circle'
            }
            if (icons[key] == undefined) {
                key  = 'Other'
            }
            return icons[key];
        }
    }

}
</script>
